import { useState, useMemo } from "react";
import type { TreeCarbonEstimate } from "@/mocks/carbono";
import CarbonConfidenceBadge from "./CarbonConfidenceBadge";

interface TreeCarbonTableProps {
  estimates: TreeCarbonEstimate[];
}

export default function TreeCarbonTable({ estimates }: TreeCarbonTableProps) {
  const [filterProject, setFilterProject] = useState("");
  const [filterMunicipality, setFilterMunicipality] = useState("");
  const [filterNeighborhood, setFilterNeighborhood] = useState("");
  const [filterSpecies, setFilterSpecies] = useState("");
  const [filterConfidence, setFilterConfidence] = useState("");
  const [filterDapMin, setFilterDapMin] = useState("");
  const [filterDapMax, setFilterDapMax] = useState("");
  const [sortKey, setSortKey] = useState<string>("co2eEstimated");
  const [sortAsc, setSortAsc] = useState(false);

  const projects = useMemo(() => [...new Set(estimates.map((e) => e.projectId))], [estimates]);
  const municipalities = useMemo(() => [...new Set(estimates.map((e) => e.municipality))], [estimates]);
  const neighborhoods = useMemo(() => [...new Set(estimates.map((e) => e.neighborhood))], [estimates]);
  const species = useMemo(() => [...new Set(estimates.map((e) => e.species))], [estimates]);

  const filtered = useMemo(() => {
    return estimates.filter((e) => {
      if (filterProject && e.projectId !== filterProject) return false;
      if (filterMunicipality && e.municipality !== filterMunicipality) return false;
      if (filterNeighborhood && e.neighborhood !== filterNeighborhood) return false;
      if (filterSpecies && e.species !== filterSpecies) return false;
      if (filterConfidence && e.confidenceLevel !== filterConfidence) return false;
      if (filterDapMin && e.dap < Number(filterDapMin)) return false;
      if (filterDapMax && e.dap > Number(filterDapMax)) return false;
      return true;
    });
  }, [estimates, filterProject, filterMunicipality, filterNeighborhood, filterSpecies, filterConfidence, filterDapMin, filterDapMax]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortKey as keyof TreeCarbonEstimate] as number | string;
      const bv = b[sortKey as keyof TreeCarbonEstimate] as number | string;
      if (typeof av === "number" && typeof bv === "number") {
        return sortAsc ? av - bv : bv - av;
      }
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [filtered, sortKey, sortAsc]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const SortIcon = ({ colKey }: { colKey: string }) => {
    if (sortKey !== colKey) return <i className="ri-arrow-up-down-line w-3 h-3 flex items-center justify-center text-foreground-300 ml-1" />;
    return sortAsc
      ? <i className="ri-arrow-up-line w-3 h-3 flex items-center justify-center text-primary-600 ml-1" />
      : <i className="ri-arrow-down-line w-3 h-3 flex items-center justify-center text-primary-600 ml-1" />;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] font-medium text-foreground-500 mb-1">Projeto</label>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-background-200 rounded-md bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">Todos</option>
              {projects.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-foreground-500 mb-1">Município</label>
            <select
              value={filterMunicipality}
              onChange={(e) => setFilterMunicipality(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-background-200 rounded-md bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">Todos</option>
              {municipalities.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-foreground-500 mb-1">Bairro</label>
            <select
              value={filterNeighborhood}
              onChange={(e) => setFilterNeighborhood(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-background-200 rounded-md bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">Todos</option>
              {neighborhoods.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-foreground-500 mb-1">Espécie</label>
            <select
              value={filterSpecies}
              onChange={(e) => setFilterSpecies(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-background-200 rounded-md bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">Todas</option>
              {species.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-foreground-500 mb-1">Confiança</label>
            <select
              value={filterConfidence}
              onChange={(e) => setFilterConfidence(e.target.value)}
              className="w-full px-2.5 py-2 text-xs border border-background-200 rounded-md bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">Todas</option>
              <option value="alta">Alta</option>
              <option value="média">Média</option>
              <option value="baixa">Baixa</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-[11px] font-medium text-foreground-500 mb-1">DAP mínimo</label>
              <input
                type="number"
                value={filterDapMin}
                onChange={(e) => setFilterDapMin(e.target.value)}
                className="w-full px-2.5 py-2 text-xs border border-background-200 rounded-md bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder="cm"
              />
            </div>
            <div className="flex-1">
              <label className="block text-[11px] font-medium text-foreground-500 mb-1">DAP máximo</label>
              <input
                type="number"
                value={filterDapMax}
                onChange={(e) => setFilterDapMax(e.target.value)}
                className="w-full px-2.5 py-2 text-xs border border-background-200 rounded-md bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder="cm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-background-50 border-b border-background-200">
                <th className="text-left px-4 py-3 font-semibold text-foreground-700">CTA</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-700">Espécie</th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("dap")}>
                  <span className="inline-flex items-center">DAP <SortIcon colKey="dap" /></span>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("height")}>
                  <span className="inline-flex items-center">Altura <SortIcon colKey="height" /></span>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("biomassEstimated")}>
                  <span className="inline-flex items-center">Biomassa <SortIcon colKey="biomassEstimated" /></span>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("carbonEstimated")}>
                  <span className="inline-flex items-center">Carbono <SortIcon colKey="carbonEstimated" /></span>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-foreground-700 cursor-pointer" onClick={() => handleSort("co2eEstimated")}>
                  <span className="inline-flex items-center">CO2e <SortIcon colKey="co2eEstimated" /></span>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-700">Metodologia</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-700">Confiança</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-700">Vistoria</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {sorted.map((e) => (
                <tr key={e.id} className="hover:bg-background-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground-800">{e.ctaCode}</div>
                    <div className="text-foreground-400 text-[11px]">{e.neighborhood}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground-800">{e.species}</div>
                    <div className="text-foreground-400 text-[11px]">{e.speciesCommon}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-foreground-700">{e.dap > 0 ? `${e.dap} cm` : "—"}</td>
                  <td className="px-4 py-3 text-right text-foreground-700">{e.height > 0 ? `${e.height} m` : "—"}</td>
                  <td className="px-4 py-3 text-right font-medium text-foreground-800">{e.biomassEstimated > 0 ? e.biomassEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 }) : "—"}</td>
                  <td className="px-4 py-3 text-right font-medium text-foreground-800">{e.carbonEstimated > 0 ? e.carbonEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 }) : "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold text-primary-700">{e.co2eEstimated > 0 ? e.co2eEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 }) : "—"}</td>
                  <td className="px-4 py-3 text-foreground-600">{e.methodology}</td>
                  <td className="px-4 py-3">
                    <CarbonConfidenceBadge level={e.confidenceLevel} />
                  </td>
                  <td className="px-4 py-3 text-foreground-500">{e.lastInspection}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 bg-background-50 border-t border-background-200 text-[11px] text-foreground-500">
          Exibindo {sorted.length} de {estimates.length} estimativas
        </div>
      </div>
    </div>
  );
}